import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import L from 'leaflet';
import { featureGroup, latLng, Map, MapOptions, tileLayer } from 'leaflet';
import * as turf from '@turf/turf';
import { finalize, takeUntil } from 'rxjs/operators';
import { PolygonSuccessDialog } from '../dialogs/polygon-success-dialog/polygon-success-dialog';
import { CalendarService, OsmService } from '../../services';
import { CityValues, OsmResponse, ShopSettings } from '../../types';
import { SimpleErrorPopup } from '@app/sharedModule/components/error-handler/simple-error-popup.component/simple-error-popup';
import { Geocoder, IGeocoder } from '@pages/checkout-app/services/geocoder/geocoder';
import {
  CollectedDataObject,
  Latlng,
  ListOutputInfo,
  Point,
  ShippingMethod,
  GEODeliveryAreaMethod,
} from '@pages/checkout-app/types/types';
import { convertDistanceUnitToMeters, DISTANCE_UNITS } from '@pages/checkout-app/utils';
import { CheckoutService } from '@pages/checkout-app/checkout.service';

export const PDQ_MARKER_PATH = '../../../../../../assets/checkout-icons/pdq-map-marker.svg';
export const COUNTY_ZOOM_LEVEL = 8;
export const mapOptions: MapOptions = {
  center: latLng(31.92937677561224, 34.805872535131286),
  zoom: 11,

  layers: [
    tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: 'Map data © OpenStreetMap contributors',
    }),
  ],
};

@Component({
  selector: 'app-polygon',
  templateUrl: './polygon.html',
  styleUrls: ['./polygon.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class Polygon implements OnInit, AfterViewInit, OnDestroy {
  public map: Map;
  @Input() renderPolygon: Subject<string>;
  @Input() stage: string;
  @Input() shopSettings: ShopSettings;
  @Input() selectedOnDrawerMode: any;
  @Input() editMode: boolean = false;
  @Input() selectedCities: string[];
  @Input() polygonInputValues: any;
  @Input() shippingMethodCategory: ShippingMethod;
  @Input() polygonRadius: string | number;
  @Input() maxPolygonRadius: number;
  @Input() polygonRadiusUnits: DISTANCE_UNITS;
  @Input() polygonAddress: any;
  @Input() loadedPolygonStage: string;
  @Input() polygonLatLngs: any;
  @Output() onShowpenToolTip = new EventEmitter<boolean>();
  @Output() onLoading = new EventEmitter<boolean>();
  @Output() invalidDraw = new EventEmitter<boolean>();
  @Output() onSetLocationSettingsValues = new EventEmitter<{}>();
  @Output() onGetCitiesListPressedFromPopUp = new EventEmitter<any>();
  @Output() onDrawerButtonClick = new EventEmitter<boolean>();
  @Output() onCityBoundary = new EventEmitter<any>();
  @Output() onLocalMethodCityChoose = new EventEmitter<any>();

  // L map options
  mapOptions = mapOptions;
  circle: any;
  marker: any;
  polygon: any;
  maskLayer: any;

  // drawer options
  drawnItems: L.FeatureGroup = featureGroup();
  drawOptions = {
    draw: {
      polyline: undefined,
      circle: undefined,
      rectangle: undefined,
      marker: undefined,
      circlemarker: undefined,
    },
    edit: undefined,
  };

  listOutputInfo: ListOutputInfo[] = [];
  showPenToolToolTip: boolean = false;
  localStreetCityName: string;
  collectedDataObject: CollectedDataObject = {};
  drawerCoordinates: Latlng[][];
  currentShopLocationCoords: Latlng = {
    lat: 0,
    lng: 0,
  };

  currentLocalCityCoordsMask = [];
  cityBoundaryPolygon: any;
  private destroy$ = new Subject();

  constructor(
    private dialog: MatDialog,
    private osmService: OsmService,
    private calenderService: CalendarService,
    private geocoder: Geocoder,
    private csService: CheckoutService,
  ) {}

  ngOnInit() {
    this.initDefaultLocationZoom();
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.onApplyGeo(this.loadedPolygonStage);
  }

  public async initDefaultLocationZoom(selectedAddress?: string, changePrimaryShopLocation: boolean = false) {
    await this.getCurrentShopLocationMarker(changePrimaryShopLocation);

    this.csService.onInitPrimaryLocationView$.pipe(takeUntil(this.destroy$)).subscribe(async (location) => {
      await this.getCurrentShopLocationMarker(true, false);
    });
  }

  async initLocalCityModeBoundary(cityValue: CityValues, isApplyClicked?: boolean): Promise<void> {
    if (this.maskLayer) {
      this.map.removeLayer(this.maskLayer);
    }

    if (isApplyClicked) {
      this.clearMap();
    }

    this.localStreetCityName = cityValue.name.trim() || '';

    L.Mask = L.Polygon.extend({
      options: {
        stroke: false,
        color: '#333',
        fillOpacity: 0.5,
        clickable: true,

        outerBounds: new L.LatLngBounds([-90, -360], [90, 360]),
      },

      initialize: function (latLngs, options) {
        const outerBoundsLatLngs = [
          this.options.outerBounds.getSouthWest(),
          this.options.outerBounds.getNorthWest(),
          this.options.outerBounds.getNorthEast(),
          this.options.outerBounds.getSouthEast(),
        ];
        L.Polygon.prototype.initialize.call(this, [outerBoundsLatLngs, latLngs], options);
      },
    });
    L.mask = function (latLngs, options) {
      return new L.Mask(latLngs, options);
    };

    const cityBoundaries = await this.osmService.generateSelectedCityBoundaries(cityValue.point).catch((err) => {
      return this.openErrorDialog('Ops..., Something went wrong. Try Again!');
    });

    this.currentLocalCityCoordsMask = [];
    if (cityBoundaries.coordinates?.length > 1) {
      cityBoundaries.coordinates.map((coords) => {
        this.currentLocalCityCoordsMask = [...this.currentLocalCityCoordsMask, ...coords];
      });
      this.maskLayer = L.mask(this.currentLocalCityCoordsMask).addTo(this.map);
    } else {
      this.currentLocalCityCoordsMask = cityBoundaries.coordinates[0];
      this.maskLayer = L.mask(cityBoundaries.coordinates[0])?.addTo(this.map);
    }
    if (cityBoundaries.type == 'Polygon') {
      this.cityBoundaryPolygon = turf.polygon(cityBoundaries);
    } else {
      this.cityBoundaryPolygon = turf.multiPolygon(cityBoundaries);
    }

    this.onCityBoundary.emit(this.cityBoundaryPolygon.geometry.coordinates);

    L.geoJSON(cityBoundaries, { color: 'green', fillOpacity: 0 }).addTo(this.map);

    this.map?.setView(new L.LatLng(cityValue?.point[0], cityValue?.point[1]), 12);
  }

  openFetchCitiesSuccess(citiesCount: number, msg: string = 'Cities'): void {
    const dialogRef = this.dialog.open(PolygonSuccessDialog, {
      data: {
        citiesCount: citiesCount,
        citiesOrStreetsMsg: msg,
      },
    });
    const detectAction = dialogRef.componentInstance?.onGetCitiesList.subscribe(() => {
      this.onGetCitiesListPressedFromPopUp.emit();
    });
    dialogRef.afterClosed().subscribe(() => {
      detectAction?.unsubscribe();
    });
  }

  onSelectPolygonMethodChange(event): void {
    if (event) {
      this.showPenToolToolTip = !!event?.value?.includes('pen_tool');
      this.onShowpenToolTip.emit(this.showPenToolToolTip);

      if (this.polygon) {
        this.map.removeLayer(this.polygon);
      }
      if (this.circle) {
        this.map.removeLayer(this.circle);
      }
    }
  }

  onMapReady(map) {
    this.map = map;
  }

  drawButtonDetect(event): void {
    if (event) {
      this.onDrawerButtonClick.emit(false);
      const scrolledY = window.scrollY;
      if (scrolledY) {
        window.scroll(0, scrolledY - 65);
      }
    }
  }

  mapClickPoint(event: any) {
    // case we local mode
    if (
      this.cityBoundaryPolygon &&
      this.shippingMethodCategory == 'local' &&
      (this.stage == 'pen_tool' || this.stage == 'states_pen_tool')
    ) {
      if (event.latlng) {
        const clickedPoint: Point = [event.latlng.lat, event.latlng.lng];

        //check if this point inside polygon
        const isPointInsidePolygon = turf.booleanPointInPolygon(
          clickedPoint,
          this.cityBoundaryPolygon.geometry.coordinates,
        );

        if (!isPointInsidePolygon) {
          const deleteLastPointElement = <HTMLElement>document.querySelector('[title="Delete last point drawn"]');
          deleteLastPointElement.click();

          return this.openErrorDialog('Please, choose draw inside city boundary');
        } else {
          // do nothing
        }
      } else {
        console.log('Error, on getting point[lat,lng] on map click');
      }
    }
  }

  onDrawCreated(e: any) {
    const { layerType, layer } = e;
    if (layerType === 'polygon') {
      const polygonCoordinates = layer._latlngs;
      this.drawerCoordinates = polygonCoordinates;
    }
    this.drawnItems.addLayer(e.layer);
  }

  public async onApplyGeo(
    stage: string,
    shippingMethod?: ShippingMethod,
    selectValueFromPolygonAutocompleteCoors?: any | Latlng,
    isApplyClicked?: boolean,
  ) {
    // on every apply reset the cities/states/list
    this.listOutputInfo = [];

    if (stage?.includes('pen_tool') && !this.drawerCoordinates && isApplyClicked) {
      this.dialog.open(PolygonSuccessDialog, {
        data: {
          msg: `Please, draw a polygon with pen tool`,
        },
      });
      this.onLoading.emit(false);
      this.invalidDraw.emit(true);
      return;
    }

    this.clearMap();

    switch (stage) {
      case GEODeliveryAreaMethod.CUSTOM_PEN_TOOL:
      case GEODeliveryAreaMethod.PEN_TOOL:
      case GEODeliveryAreaMethod.STATES_PEN_TOOL: {
        if (this.editMode && !this.drawerCoordinates && !isApplyClicked) {
          // init polygon on drawer mode
          if (this.selectedOnDrawerMode) {
            this.generatePolygonOnMap(this.selectedOnDrawerMode);
          }

          // on local method init city boundary mask
          if (this.shippingMethodCategory == ShippingMethod.LOCAL && !stage.includes('custom')) {
            await this.initLocalCityModeBoundary(this.polygonInputValues[0]?.localMethodCityChoose, isApplyClicked);
          }
        }
        if (isApplyClicked) {
          const drawerSelectPoints: Point[] = [];

          if (this.shippingMethodCategory !== ShippingMethod.LOCAL && stage !== GEODeliveryAreaMethod.CUSTOM_PEN_TOOL) {
            this.listOutputInfo = await this.generateParsedAddressFromPoints(this.drawerCoordinates[0], stage);
          }

          for (const coors of this.drawerCoordinates[0]) {
            drawerSelectPoints.push([coors.lat, coors.lng]);
          }

          if (stage === GEODeliveryAreaMethod.CUSTOM_PEN_TOOL) {
            this.generatePolygonOnMap(drawerSelectPoints);
            this.onLoading.emit(false);
            this.collectedDataObject = {
              coordinates: drawerSelectPoints ?? [],
              currentLocalCityCoordsMask: this.currentLocalCityCoordsMask,
            };
            this.onSetLocationSettingsValues.emit(this.collectedDataObject);
            return;
          }

          await this.osmService
            .getOsmDataForPolygon(
              this.convertCoordinatesForOsm(this.drawerCoordinates[0])?.trim(),
              shippingMethod,
              stage,
            )
            .pipe(
              finalize(() => {
                this.onLoading.emit(false);
                this.collectedDataObject = {
                  cities: this.listOutputInfo,
                  coordinates: drawerSelectPoints ?? [],
                  currentLocalCityCoordsMask: this.currentLocalCityCoordsMask,
                };
                this.onSetLocationSettingsValues.emit(this.collectedDataObject);
              }),
            )
            .subscribe(
              (res: OsmResponse) => {
                this.generateCityOrStreetsName(res.elements, shippingMethod);
                this.generatePolygonOnMap(drawerSelectPoints);
                this.openFetchCitiesSuccess(
                  this.listOutputInfo?.length,
                  this.getShippingMethodDisplayText(shippingMethod, stage),
                );
              },
              (error) => {
                console.log('Error: ' + error);
                this.onLoading.emit(false);
                this.openErrorDialog('Ops..., Something went wrong. Try Again!');
              },
            );
        }
        break;
      }
      case GEODeliveryAreaMethod.STREETS_FROM_LIST:
      case GEODeliveryAreaMethod.CITIES_FROM_LIST:
      case GEODeliveryAreaMethod.STATES_FROM_LIST: {
        if (this.editMode && !isApplyClicked) {
          if (this.polygonLatLngs) {
            const turfPolygon: any = this.convertCoordinatesToCollection(this.polygonLatLngs);
            this.generatePolygonOnMap([turfPolygon.geometry.coordinates[0]]);
          }

          // on local method init city boundary mask
          if (this.shippingMethodCategory == ShippingMethod.LOCAL && !stage.includes('custom')) {
            await this.initLocalCityModeBoundary(this.polygonInputValues[0].localMethodCityChoose, isApplyClicked);
          }
        }
        if (isApplyClicked) {
          const pointsOfSelectedZones: Point[] = [];

          if (this.shippingMethodCategory !== ShippingMethod.LOCAL) {
            this.listOutputInfo = await this.generateParsedAddressFromPoints(
              selectValueFromPolygonAutocompleteCoors,
              stage,
            );
          }

          this.osmService
            .getOsmDataForPolygon(
              this.convertCoordinatesForOsm(selectValueFromPolygonAutocompleteCoors)?.trim(),
              shippingMethod,
              stage,
            )
            .pipe(
              finalize(() => {
                this.onLoading.emit(false);

                this.collectedDataObject = {
                  cities: this.listOutputInfo,
                  selectedCities: this.selectedCities,
                  selectedCitiesCoors: pointsOfSelectedZones,
                  cityBoundary: this.cityBoundaryPolygon ?? null,
                  currentLocalCityCoordsMask: this.currentLocalCityCoordsMask,
                };
                this.onSetLocationSettingsValues.emit(this.collectedDataObject);
              }),
            )
            .subscribe(
              (res: OsmResponse) => {
                this.generateCityOrStreetsName(res.elements, shippingMethod);

                selectValueFromPolygonAutocompleteCoors.map((coords: any) => {
                  const point: Point = [coords.lat, coords.lng];
                  pointsOfSelectedZones.push(point);
                });

                // convert coords to concave collection
                const polygon: any = this.convertCoordinatesToCollection(pointsOfSelectedZones);

                this.generatePolygonOnMap(polygon.geometry?.coordinates[0]);

                this.openFetchCitiesSuccess(
                  this.listOutputInfo?.length,
                  this.getShippingMethodDisplayText(shippingMethod, stage),
                );
              },
              (error) => {
                console.log(error);
                this.onLoading.emit(false);
                this.openErrorDialog('Ops..., Something went wrong. Try Again!');
              },
            );
        }
        break;
      }
      case GEODeliveryAreaMethod.CUSTOM_FROM_ADDRESS_RADIUS:
      case GEODeliveryAreaMethod.STREETS_FROM_ADDRESS_RADIUS:
      case GEODeliveryAreaMethod.STATES_FROM_ADDRESS_RADIUS:
      case GEODeliveryAreaMethod.CITIES_FROM_ADDRESS_RADIUS: {
        if (this.editMode && !isApplyClicked) {
          if (
            this.polygonLatLngs &&
            (this.shippingMethodCategory != ShippingMethod.LOCAL || stage.includes('custom'))
          ) {
            if (+this.polygonRadius != 0) {
              this.generateMultiPolygonOnMap(this.polygonLatLngs);
              return;
            }

            const selectedRadius = this.maxPolygonRadius ?? this.polygonRadius;
            const radiusInMeters = convertDistanceUnitToMeters(+selectedRadius, this.polygonRadiusUnits);
            this.generateCircleOnMap(this.polygonInputValues[0].latlng, radiusInMeters);
          }

          if (this.shippingMethodCategory == ShippingMethod.LOCAL && !stage.includes('custom')) {
            await this.initLocalCityModeBoundary(this.polygonInputValues[0]?.localMethodCityChoose, isApplyClicked);
            this.generatePolygonOnMap(this.polygonInputValues[0].polygonIntersection.geometry.coordinates[0]);
          }
        }
        if (isApplyClicked) {
          let isCityBoundaryIntersectsWithCircle: any;
          // by default select the shop location as address + coors
          if (!(selectValueFromPolygonAutocompleteCoors.lat && selectValueFromPolygonAutocompleteCoors.lng)) {
            selectValueFromPolygonAutocompleteCoors.lat = +this.currentShopLocationCoords?.lat;
            selectValueFromPolygonAutocompleteCoors.lng = +this.currentShopLocationCoords?.lng;
            selectValueFromPolygonAutocompleteCoors.address = this.shopSettings.primaryLocation;
          }
          const point: [number, number] = [
            +selectValueFromPolygonAutocompleteCoors.lat,
            +selectValueFromPolygonAutocompleteCoors.lng,
          ];

          if (
            selectValueFromPolygonAutocompleteCoors?.maxRadius > 0 &&
            selectValueFromPolygonAutocompleteCoors?.radius > 0
          ) {
            this.createCircleRing(selectValueFromPolygonAutocompleteCoors, point);
          }

          if (shippingMethod == ShippingMethod.LOCAL && !stage.includes('custom')) {
            const createdCircleOnLocalMethod = this.createCircleOnLocalMethod(
              selectValueFromPolygonAutocompleteCoors,
              point,
            );
            isCityBoundaryIntersectsWithCircle = createdCircleOnLocalMethod.isCityBoundaryIntersectsWithCircle;
            selectValueFromPolygonAutocompleteCoors.localCityValue = createdCircleOnLocalMethod.localCityValue;
          }

          // enter on regular radius method when radius is 0
          if (selectValueFromPolygonAutocompleteCoors?.radius == 0) {
            // set radius as the maximum radius
            await this.createCircle(
              selectValueFromPolygonAutocompleteCoors,
              point,
              isCityBoundaryIntersectsWithCircle,
              stage,
              this.shippingMethodCategory,
            );
          }
        }
        break;
      }
      case GEODeliveryAreaMethod.COUNTY: {
        if (this.editMode && !isApplyClicked) {
          if (this.polygonLatLngs) {
            this.generatePolygonOnMap(this.polygonLatLngs);
          }
          return;
        }

        const { point, name } = selectValueFromPolygonAutocompleteCoors;
        const optionParams: IGeocoder = {
          point: {
            lat: point[0],
            lng: point[1],
          },
          zoom: COUNTY_ZOOM_LEVEL,
        };

        this.geocoder
          .reverse(optionParams)
          .then((result) => {
            const countyBoundaryCoords: any = result?.geojson?.coordinates ?? [];
            const reversedCoords = countyBoundaryCoords[0].map((val) => val.reverse());
            this.generatePolygonOnMap(reversedCoords);
            this.onLoading.emit(false);
            this.collectedDataObject = {
              countyCoordinates: reversedCoords ?? [],
              address: name,
            };
            this.onSetLocationSettingsValues.emit(this.collectedDataObject);
          })
          .catch((err) => {
            console.log(err);
            this.onLoading.emit(false);
            this.openErrorDialog('Ops..., Something went wrong. Try Again!');
          });
      }
    }
  }

  async createCircle(
    selectValueFromPolygonAutocompleteCoors,
    point: Point,
    isCityBoundaryIntersectsWithCircle: any,
    stage: string,
    shippingMethod: ShippingMethod,
  ): Promise<void> {
    if (
      this.shippingMethodCategory !== ShippingMethod.LOCAL &&
      stage !== GEODeliveryAreaMethod.CUSTOM_FROM_ADDRESS_RADIUS
    ) {
      this.listOutputInfo = await this.generateParsedAddressFromPoints([{ lat: point[0], lng: point[1] }], stage);
    }

    if (stage === GEODeliveryAreaMethod.CUSTOM_FROM_ADDRESS_RADIUS) {
      this.onLoading.emit(false);
      this.collectedDataObject = {
        radius: selectValueFromPolygonAutocompleteCoors.radius,
        maxRadius: selectValueFromPolygonAutocompleteCoors.maxRadius,
        radiusUnits: this.polygonRadiusUnits,
        address: this.polygonAddress?.formatted_address
          ? this.polygonAddress?.formatted_address
          : selectValueFromPolygonAutocompleteCoors?.address,
        latlng: point,
        polygonIntersection: isCityBoundaryIntersectsWithCircle ? isCityBoundaryIntersectsWithCircle : null,
        cityBoundary: this.cityBoundaryPolygon ? this.cityBoundaryPolygon : null,
        currentLocalCityCoordsMask: this.currentLocalCityCoordsMask,
      };

      this.onSetLocationSettingsValues.emit(this.collectedDataObject);

      if (shippingMethod == ShippingMethod.LOCAL && !stage.includes('custom')) {
        // case we in local mode
        this.generatePolygonOnMap(isCityBoundaryIntersectsWithCircle.geometry.coordinates[0]);
      } else {
        const radiusInMeters = convertDistanceUnitToMeters(
          +selectValueFromPolygonAutocompleteCoors.maxRadius,
          this.polygonRadiusUnits,
        );
        // case we in domestic mode
        this.generateCircleOnMap(point, radiusInMeters);
      }
      return;
    }

    this.osmService
      .getOsmDataForPolygon(
        selectValueFromPolygonAutocompleteCoors?.localCityValue
          ? selectValueFromPolygonAutocompleteCoors.localCityValue
          : selectValueFromPolygonAutocompleteCoors,
        shippingMethod,
        stage,
      )
      .pipe(
        finalize(() => {
          this.onLoading.emit(false);
          this.collectedDataObject = {
            cities: this.listOutputInfo,
            radius: selectValueFromPolygonAutocompleteCoors.radius,
            maxRadius: selectValueFromPolygonAutocompleteCoors.maxRadius,
            radiusUnits: this.polygonRadiusUnits,
            address: this.polygonAddress?.formatted_address
              ? this.polygonAddress?.formatted_address
              : selectValueFromPolygonAutocompleteCoors?.address,
            latlng: point,
            polygonIntersection: isCityBoundaryIntersectsWithCircle ? isCityBoundaryIntersectsWithCircle : null,
            cityBoundary: this.cityBoundaryPolygon ? this.cityBoundaryPolygon : null,
            currentLocalCityCoordsMask: this.currentLocalCityCoordsMask,
          };

          this.onSetLocationSettingsValues.emit(this.collectedDataObject);
        }),
      )
      .subscribe(
        (res: any) => {
          this.generateCityOrStreetsName(res.elements, shippingMethod);

          if (shippingMethod == ShippingMethod.LOCAL) {
            // case we in local mode
            this.generatePolygonOnMap(isCityBoundaryIntersectsWithCircle.geometry.coordinates[0]);
          } else {
            const radiusInMeters = convertDistanceUnitToMeters(
              +selectValueFromPolygonAutocompleteCoors.maxRadius,
              this.polygonRadiusUnits,
            );
            // case we in domestic mode
            this.generateCircleOnMap(point, radiusInMeters);
          }

          this.openFetchCitiesSuccess(
            this.listOutputInfo?.length,
            this.getShippingMethodDisplayText(shippingMethod, stage),
          );
        },
        (error) => {
          console.log(error);
          this.onLoading.emit(false);
          this.openErrorDialog('Ops..., Something went wrong. Try Again!');
        },
      );
  }

  createCircleOnLocalMethod(selectValueFromPolygonAutocompleteCoors: any, point: Point): any {
    const circlePolygon = turf.circle(point.reverse(), selectValueFromPolygonAutocompleteCoors.maxRadius, {
      units: this.polygonRadiusUnits,
    });
    point.reverse();

    const isCityBoundaryIntersectsWithCircle = turf.intersect(
      turf.flip(circlePolygon),
      this.cityBoundaryPolygon.geometry.coordinates,
    );

    console.log('point' + point + ' intersect city boundary' + isCityBoundaryIntersectsWithCircle);

    if (isCityBoundaryIntersectsWithCircle) {
      const predictedPolygonCoordinatesArray = isCityBoundaryIntersectsWithCircle?.geometry?.coordinates;

      // on length greater than 1 , its multipolygon  , else polygon
      const mostPredictedCoordinates =
        predictedPolygonCoordinatesArray.length > 1
          ? predictedPolygonCoordinatesArray[0][0]
          : predictedPolygonCoordinatesArray[0];

      const localCityValue = this.convertCoordinatesForOsm(mostPredictedCoordinates, true);

      return { isCityBoundaryIntersectsWithCircle, localCityValue };
    } else {
      this.onLoading.emit(false);
      return this.openErrorDialog('Please choose street inside this selected local city!');
    }
  }

  createCircleRing(selectValueFromPolygonAutocompleteCoors: any, point: Point): void {
    const reversedPoint = point.reverse();

    const innerPolygonCircle = turf.circle(reversedPoint, selectValueFromPolygonAutocompleteCoors.radius, {
      units: selectValueFromPolygonAutocompleteCoors.radiusUnits,
    });

    const outerPolygonCircle = turf.circle(reversedPoint, selectValueFromPolygonAutocompleteCoors.maxRadius, {
      units: selectValueFromPolygonAutocompleteCoors.radiusUnits,
    });

    const circleMultiPolygon = turf.multiPolygon([
      [outerPolygonCircle.geometry.coordinates[0]],
      [innerPolygonCircle.geometry.coordinates[0]],
    ]);

    this.generateMultiPolygonOnMap(circleMultiPolygon);
    this.onLoading.emit(false);
    this.collectedDataObject = {
      cities: this.listOutputInfo,
      radius: selectValueFromPolygonAutocompleteCoors.radius,
      maxRadius: selectValueFromPolygonAutocompleteCoors.maxRadius,
      radiusUnits: this.polygonRadiusUnits,
      address: this.polygonAddress?.formatted_address
        ? this.polygonAddress?.formatted_address
        : selectValueFromPolygonAutocompleteCoors?.address,
      latlng: point,
      circleRingBoundary: circleMultiPolygon,
    };

    this.onSetLocationSettingsValues.emit(this.collectedDataObject);
    return;
  }

  async getCurrentShopLocationMarker(
    changePrimaryShopLocation: boolean = false,
    clearMap: boolean = true,
  ): Promise<void> {
    if (this.marker) {
      this.map.removeLayer(this.marker);
    }

    if (clearMap) {
      this.clearMap();
    }

    const parsedAddress: any = await this.calenderService.parseGoogleGeocode(this.shopSettings.primaryLocation);
    const { lat, lng } = parsedAddress.results[0]?.geometry?.location;
    console.log(`current address ${this.shopSettings.primaryLocation} and coords ${lat} ,${lng}`);

    this.currentShopLocationCoords = {
      lat: lat,
      lng: lng,
    };

    this.map.setView(new latLng(lat, lng), 12);

    const markerIcon = L.icon({
      iconUrl: PDQ_MARKER_PATH,
      iconSize: [30, 30],
    });

    this.marker = L.marker([lat, lng], { icon: markerIcon }).addTo(this.map);

    // on local mode
    if (
      (this.shippingMethodCategory == 'local' && !this.editMode) ||
      (changePrimaryShopLocation && this.shippingMethodCategory == 'local')
    ) {
      const localModeCityValue: CityValues = {
        name: this.shopSettings.primaryLocation,
        point: [this.currentShopLocationCoords.lat, this.currentShopLocationCoords.lng],
      };
      this.onLocalMethodCityChoose.emit(localModeCityValue);

      if (this.stage !== GEODeliveryAreaMethod.COUNTY && !this.stage?.includes('custom')) {
        console.log('init city boundary');
        this.initLocalCityModeBoundary(localModeCityValue);
      }
    }
  }

  //coords string for OSM
  convertCoordinatesForOsm(coordinates: any[], turfPolygon: boolean = false): string {
    let object: string = '';

    // for osm , the coords not needed to be closed
    if (turfPolygon) {
      coordinates.splice(-1);
    }

    coordinates.map((coor: any) => {
      const lat = coor.lat ? coor.lat : coor[0];
      const lng = coor.lng ? coor.lng : coor[1];
      object += lat + ' ' + lng + ' ';
    });
    return object.trim();
  }

  openErrorDialog(message: string) {
    this.dialog.open(SimpleErrorPopup, {
      data: {
        msg: message,
      },
    });
  }

  convertCoordinatesToCollection(coords: any) {
    const collection: any = [];
    try {
      coords.map((coord) => collection.push(turf.point(coord)));
      const points = turf.featureCollection(collection);
      const polygon = turf.concave(points as any);
      return polygon;
    } catch (e) {
      console.log('error:' + e);
      return;
    }
  }

  generateCityOrStreetsName(elements: any, deliveryMethod: string): void {
    let eventInsideAlready;
    let cityOrStreetNameObject = {};

    // we have 4 cases for now, shop country and local\domestic
    switch (this.shopSettings.shopCountryName) {
      case 'Israel': {
        elements.map((element: any) => {
          if (element?.tags['name:he']) {
            eventInsideAlready = this.listOutputInfo.find((val) => val == element?.tags['name:he']);

            if (!eventInsideAlready) {
              if (deliveryMethod == 'local') {
                cityOrStreetNameObject = {
                  hebrew: element?.tags['name:he'],
                  english: element?.tags['name:en'] + ', ' + this.localStreetCityName,
                };
              } else {
                cityOrStreetNameObject = {
                  hebrew: element?.tags['name:he'],
                  english: element?.tags['name:en'],
                };
              }
              this.listOutputInfo.push(cityOrStreetNameObject);
            }
          }
        });
        break;
      }
      case 'United States': {
        elements.map((element: any) => {
          const { name } = element?.tags;

          if (name) {
            eventInsideAlready = this.listOutputInfo.find((val) => val.english.includes(name));

            if (!eventInsideAlready) {
              if (deliveryMethod == 'local') {
                cityOrStreetNameObject = {
                  english: name + ', ' + this.localStreetCityName,
                };
              } else {
                cityOrStreetNameObject = {
                  english: name,
                };
              }
              this.listOutputInfo.push(cityOrStreetNameObject);
            }
          }
        });
        break;
      }
    }
  }

  clearMap(forceClear: boolean = false): void {
    if (this.drawnItems) {
      this.drawnItems.clearLayers();
    }

    if (this.polygon) {
      this.map.removeLayer(this.polygon);
    }

    if (this.circle) {
      this.map.removeLayer(this.circle);
    }

    if (this.maskLayer && (this.shippingMethodCategory != ShippingMethod.LOCAL || forceClear)) {
      this.map.removeLayer(this.maskLayer);
    }
  }

  generateMultiPolygonOnMap(coordinates: any): void {
    const randomPointInsidePolygon = [];
    this.polygon = L.geoJSON(coordinates, {
      color: 'blue',
    }).addTo(this.map);
    this.map.fitBounds(this.polygon.getBounds());

    if (randomPointInsidePolygon?.length) {
      this.map.setView(new latLng(randomPointInsidePolygon[0], randomPointInsidePolygon[1]), 9);
    }
  }

  // init polygon on map
  generatePolygonOnMap(coordinates: any | []): void {
    const randomPointInsidePolygon = coordinates[0] ?? [];
    this.polygon = L.polygon(coordinates, {
      color: 'blue',
    }).addTo(this.map);
    this.map.fitBounds(this.polygon.getBounds());

    if (randomPointInsidePolygon?.length) {
      this.map.setView(new latLng(randomPointInsidePolygon[0], randomPointInsidePolygon[1]), 8);
    }
  }

  // init circle on map
  generateCircleOnMap(coordinates: any | [], radius): void {
    this.circle = L.circle(coordinates, radius, {
      color: 'blue',
    }).addTo(this.map);
    this.map.fitBounds(this.circle.getBounds());
  }

  getShippingMethodDisplayText(shippingMethod: ShippingMethod, stage: string): string {
    if (stage?.includes('states')) {
      return 'States';
    }
    if (shippingMethod.includes('domestic')) {
      return 'Cities';
    } else {
      return 'Streets';
    }
  }

  async generateParsedAddressFromPoints(coordinates: any, stage): Promise<ListOutputInfo[]> {
    let parsedAddressInfo = null;
    const parsedAddressInfoArray: ListOutputInfo[] = [];

    for (const coords of coordinates) {
      const result = await this.geocoder.reverse({ point: coords });

      if (stage?.includes('states')) {
        parsedAddressInfo = result?.address?.state;
      } else {
        parsedAddressInfo = result?.address?.city ?? result?.address?.town;
      }
      const isStateAlreadyInArray = parsedAddressInfoArray.find((state) => state.english == parsedAddressInfo);
      if (!isStateAlreadyInArray && parsedAddressInfo) {
        parsedAddressInfoArray.push({ english: parsedAddressInfo });
      }
    }
    return parsedAddressInfoArray;
  }
}
